import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../hooks/useTheme';
import MagneticButton from '../../../components/base/MagneticButton';
import { useState } from 'react';

export default function Footer() {
  const { t, i18n } = useTranslation();
  const { isDark } = useTheme();
  const [copiedItem, setCopiedItem] = useState<string | null>(null);

  const copyToClipboard = (text: string, item: string) => {
    navigator.clipboard.writeText(text);
    setCopiedItem(item);
    setTimeout(() => setCopiedItem(null), 2000);
  };

  const socialLinks = [
    { icon: 'ri-twitter-x-line', label: 'X (Twitter)', url: 'https://x.com/RoonChain' },
    { icon: 'ri-discord-line', label: 'Discord', url: 'https://discord.gg/AUwfdvzrEy' },
    { icon: 'ri-telegram-line', label: 'Telegram Channel', url: 'https://t.me/roonchain' },
    { icon: 'ri-group-line', label: 'Telegram Group', url: 'https://t.me/roonchainchat' },
  ];

  const networkInfo = [
    { label: t('footer.network.name'), value: 'Roon Mainnet' },
    { label: t('footer.network.symbol'), value: 'Roon' },
    { label: t('footer.network.chainId'), value: '1492' },
    { label: 'RPC', value: 'https://rpc.roonchain.com/', copyable: true },
    { label: t('footer.network.explorer'), value: 'https://browser.roonchain.com/', link: true },
  ];

  const contracts = [
    { label: 'USDR', address: '0xbC75b416D9b4045BBF31d3F9DA7eD727aFCa2f79' },
    { label: t('footer.contracts.nodeNft'), address: '0x027e7dE1469E0fd88e5EE4b5AebB596a0cb7e1C6' },
  ];

  const quickLinks = [
    { label: 'SWAP', url: 'https://web3.roonchain.com/swap/#/NSwap' },
    { label: 'Bridge', url: 'https://web3.roonchain.com/bridge/#/ignis' },
    { label: t('footer.links.explorer'), url: 'https://browser.roonchain.com/' },
  ];

  const emails = [
    { email: 'rwa@roonchain.com', desc: t('footer.email.rwa') },
    { email: 'tech@roonchain.com', desc: t('footer.email.tech') },
    { email: 'biz@roonchain.com', desc: t('footer.email.biz') },
    { email: 'legal@roonchain.com', desc: t('footer.email.legal') },
    { email: 'contact@roonchain.com', desc: t('footer.email.contact') },
  ];

  return (
    <footer
      className={`relative overflow-hidden transition-colors duration-500 ${
        isDark ? 'bg-[#0d1117]' : 'bg-[#f8fafb]'
      }`}
    >
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 md:py-16 relative z-10">
        {/* Top Section - Logo & Social */}
        <div
          className={`flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 sm:gap-6 mb-8 sm:mb-10 pb-8 sm:pb-10 border-b ${
            isDark ? 'border-white/10' : 'border-[#0d1117]/10'
          }`}
        >
          {/* Logo */}
          <a href="#" className="group inline-flex items-center gap-2 sm:gap-2.5 cursor-pointer">
            <div className="relative w-8 h-8 sm:w-9 sm:h-9 rounded-lg flex items-center justify-center transition-all duration-500 group-hover:scale-110 bg-white p-1">
              <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-[#E84A1F]/0 to-[#F06030]/0 group-hover:from-[#E84A1F]/30 group-hover:to-[#F06030]/30 blur-md transition-all duration-500 scale-0 group-hover:scale-150"></div>
              <img 
                src="https://static.readdy.ai/image/66a2b9cfb9b417b2846085a20d5b8a97/d509b5c7881d9da6657335b51d03b561.png" 
                alt="RoonChain Logo" 
                className="w-full h-full object-contain relative z-10 transition-transform duration-500 group-hover:rotate-[360deg] group-hover:scale-110"
              />
            </div>
            <span
              className={`text-lg sm:text-xl font-bold tracking-wide transition-all duration-500 group-hover:text-[#E84A1F] ${
                isDark ? 'text-white' : 'text-[#0d1117]'
              }`}
            >
              RoonChain
            </span>
          </a>

          {/* Social Icons */}
          <div className="flex items-center gap-2 sm:gap-3">
            {socialLinks.map((social, index) => (
              <MagneticButton
                key={index}
                as="a"
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                intensity={0.5}
                scale={1.2}
                className={`group relative w-9 h-9 sm:w-10 sm:h-10 border rounded-lg flex items-center justify-center overflow-hidden transition-colors duration-300 hover:text-[#E84A1F] hover:border-[#E84A1F]/30 active:scale-95 ${
                  isDark ? 'border-white/10 text-gray-400' : 'border-[#0d1117]/10 text-[#0d1117]/40'
                }`}
                title={social.label}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-[#E84A1F]/10 to-[#F06030]/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <i className={`${social.icon} text-base sm:text-lg relative z-10 group-hover:scale-110 transition-transform`}></i>
              </MagneticButton>
            ))}
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 lg:gap-6 mb-8 sm:mb-10">
          {/* Network Info */}
          <div>
            <h4
              className={`text-xs sm:text-sm font-semibold mb-3 sm:mb-4 tracking-wide flex items-center gap-2 ${
                isDark ? 'text-white' : 'text-[#0d1117]'
              }`}
            >
              <i className="ri-global-line text-[#E84A1F]"></i>
              {t('footer.network.title')}
            </h4>
            <ul className="space-y-2 sm:space-y-2.5">
              {networkInfo.map((item, index) => (
                <li key={index} className="flex flex-col gap-0.5">
                  <span className={`text-[9px] sm:text-[10px] uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-[#0d1117]/40'}`}>
                    {item.label}
                  </span>
                  {item.link ? (
                    <a
                      href={item.value}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`text-[11px] sm:text-xs hover:text-[#E84A1F] transition-colors break-all ${
                        isDark ? 'text-gray-300' : 'text-[#0d1117]/70'
                      }`}
                    >
                      {item.value}
                    </a>
                  ) : item.copyable ? (
                    <button
                      onClick={() => copyToClipboard(item.value, `network-${index}`)}
                      className={`text-[11px] sm:text-xs text-left hover:text-[#E84A1F] transition-colors break-all flex items-center gap-1 group ${
                        isDark ? 'text-gray-300' : 'text-[#0d1117]/70'
                      }`}
                    >
                      <span className="truncate">{item.value}</span>
                      <i className={`${copiedItem === `network-${index}` ? 'ri-check-line text-green-500' : 'ri-file-copy-line opacity-0 group-hover:opacity-100'} text-[10px] transition-opacity flex-shrink-0`}></i>
                    </button>
                  ) : (
                    <span className={`text-[11px] sm:text-xs ${isDark ? 'text-gray-300' : 'text-[#0d1117]/70'}`}>
                      {item.value}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Contracts */}
          <div>
            <h4
              className={`text-xs sm:text-sm font-semibold mb-3 sm:mb-4 tracking-wide flex items-center gap-2 ${
                isDark ? 'text-white' : 'text-[#0d1117]'
              }`}
            >
              <i className="ri-file-code-line text-[#E84A1F]"></i>
              {t('footer.contracts.title')}
            </h4>
            <ul className="space-y-2.5 sm:space-y-3">
              {contracts.map((contract, index) => (
                <li key={index} className="flex flex-col gap-0.5">
                  <span className={`text-[9px] sm:text-[10px] uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-[#0d1117]/40'}`}>
                    {contract.label}
                  </span>
                  <button
                    onClick={() => copyToClipboard(contract.address, `contract-${index}`)}
                    className={`text-[11px] sm:text-xs text-left hover:text-[#E84A1F] transition-colors flex items-center gap-1 group ${
                      isDark ? 'text-gray-300' : 'text-[#0d1117]/70'
                    }`}
                  >
                    <span className="font-mono truncate">{contract.address.slice(0, 8)}...{contract.address.slice(-6)}</span>
                    <i className={`${copiedItem === `contract-${index}` ? 'ri-check-line text-green-500' : 'ri-file-copy-line opacity-0 group-hover:opacity-100'} text-[10px] transition-opacity flex-shrink-0`}></i>
                  </button>
                </li>
              ))}
            </ul>

            {/* Quick Links */}
            <h4
              className={`text-xs sm:text-sm font-semibold mt-5 sm:mt-6 mb-3 sm:mb-4 tracking-wide flex items-center gap-2 ${
                isDark ? 'text-white' : 'text-[#0d1117]'
              }`}
            >
              <i className="ri-links-line text-[#E84A1F]"></i>
              {t('footer.links.title')}
            </h4>
            <ul className="space-y-1.5 sm:space-y-2">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <a
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`text-[11px] sm:text-xs hover:text-[#E84A1F] transition-colors flex items-center gap-1.5 ${
                      isDark ? 'text-gray-400' : 'text-[#0d1117]/50'
                    }`}
                  >
                    <i className="ri-arrow-right-up-line text-[10px]"></i>
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Emails */}
          <div className="sm:col-span-2">
            <h4
              className={`text-xs sm:text-sm font-semibold mb-3 sm:mb-4 tracking-wide flex items-center gap-2 ${
                isDark ? 'text-white' : 'text-[#0d1117]'
              }`}
            >
              <i className="ri-mail-line text-[#E84A1F]"></i>
              {t('footer.email.title')}
            </h4>
            <div className="grid grid-cols-1 xs:grid-cols-2 gap-2 sm:gap-3">
              {emails.map((item, index) => (
                <a
                  key={index}
                  href={`mailto:${item.email}`}
                  className={`group p-2.5 sm:p-3 rounded-lg border transition-all duration-300 hover:border-[#E84A1F]/30 ${
                    isDark 
                      ? 'border-white/5 bg-white/[0.02] hover:bg-white/[0.04]' 
                      : 'border-[#0d1117]/5 bg-[#0d1117]/[0.02] hover:bg-[#0d1117]/[0.04]'
                  }`}
                >
                  <div className={`text-[11px] sm:text-xs font-medium group-hover:text-[#E84A1F] transition-colors truncate ${
                    isDark ? 'text-gray-200' : 'text-[#0d1117]/80'
                  }`}>
                    {item.email}
                  </div>
                  <div className={`text-[9px] sm:text-[10px] mt-0.5 truncate ${isDark ? 'text-gray-500' : 'text-[#0d1117]/40'}`}>
                    {item.desc}
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom Section - Copyright Notice */}
        <div
          className={`pt-6 sm:pt-8 border-t ${
            isDark ? 'border-white/10' : 'border-[#0d1117]/10'
          }`}
        >
          <div
            className={`text-[10px] sm:text-xs leading-relaxed ${
              isDark ? 'text-gray-500' : 'text-[#0d1117]/40'
            }`}
          >
            {i18n.language === 'zh' ? (
              <>
                <div className="font-semibold mb-2">版權聲明</div>
                <div className="mb-2">© RoonChain. All rights reserved.</div>
                <div className="mb-1">
                  本網站及其所包含的全部內容，包括但不限於文字、圖表、架構說明、技術描述、系統設計、頁面佈局與視覺元素，均受適用的著作權法律及相關法律法規保護。
                </div>
                <div className="mb-1">
                  未經事先書面許可，任何個人或機構不得以任何形式複製、轉載、修改、傳播或用於商業用途。
                </div>
                <div>
                  本網站內容僅用於技術與資訊展示，不構成任何形式的投資建議、要約或承諾。
                </div>
              </>
            ) : (
              <>
                <div className="font-semibold mb-2">Copyright Notice</div>
                <div className="mb-2">© RoonChain. All rights reserved.</div>
                <div className="mb-1">
                  All content on this website, including but not limited to text, diagrams, technical descriptions, system designs, layouts, and visual elements, is protected by applicable copyright laws and regulations.
                </div>
                <div className="mb-1">
                  No part of this website may be reproduced, distributed, modified, or used for commercial purposes without prior written permission.
                </div>
                <div>
                  The information provided on this website is for technical and informational purposes only and does not constitute investment advice, an offer, or a solicitation.
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
}
